import axios from "axios";
import store from "@/store";
import testDetails from "@/static/testDetails.json";
import { computed } from "vue";
import { usePatientStore } from "@/store/health/blood";
import endpoints from "@/endpoints";
import apiClient from "@/axios.js";
const { VUE_APP_API } = process.env;

// const patientDetails = {
//     userUuid: store.getters.account.uuid,
//     firstName: store.getters.account.firstName,
//     lastName: store.getters.account.lastName,
//     email: store.getters.account.email,
//     phone: "",
//     bioSex: store.getters.profile.boi,
//     dateOfBirth: store.getters.profile.dateOfBirth,
//     region: store.getters.account.region
// }

// const initialPatient = {
//     userUuid: "",
//     firstName: "",
//     lastName: "",
//     email: "",
//     bioSex: "",
//     dateOfBirth: "",
//     appointmentInfo: "",
//     tests: [],
//     questionnaireAnswers: {},
//     ageGroup: "",
//     geneticTest: {
//         result: {
//             results: [],
//             hasAnyResult: false
//         }
//     },
//     phone: "",
//     region: "",
//     linkedProfile: "",
// };

// let patient = { ...initialPatient };

/**
 * The id this profile's health record is filed under, for the two decisions that must not
 * guess: creating the record, and telling checkout whose record a purchase belongs to.
 *
 * The rules are getHealthUserId's, which the reads use: a profile the account does not own
 * is filed under its barcode; an account's only owned profile under the account's user
 * uuid; with several owned profiles, the one the account's own record is linked to gets the
 * account uuid and the rest their barcode.
 *
 * getHealthUserId settles that last case from whatever primary patient is in the store, and
 * falls back to the barcode when there is none - including when loading it just failed. For
 * customers who already had a record under their account, that fallback created a second
 * one under the barcode, and the free report was delivered to it where they never saw it.
 * Here the account's record is asked for directly: missing (404) is an answer, any other
 * failure means it can't be told, and the caller does nothing rather than guess.
 *
 * @returns {Promise<string|null>} a user uuid or a barcode, or null when it can't be told
 */
export async function resolveHealthUserId() {
    const profile = store.getters.profile;
    const barcode = profile?.profile?.barcode;
    const accountUuid = store.getters.account?.uuid;
    if (!barcode || !accountUuid) return null;
    if (profile?.permission?.type !== 'OWNER') return barcode;

    let owners = store.getters.owners;
    if (!Array.isArray(owners)) {
        await store.dispatch("fetchAllOwners");
        owners = store.getters.owners;
    }
    if (!Array.isArray(owners)) return null;
    if (owners.length <= 1) return accountUuid;

    try {
        const { data } = await makeHealthRequest({ path: `/patients/${accountUuid}`, method: "GET" });
        return data?.linkedProfile === barcode ? accountUuid : barcode;
    } catch (error) {
        if (error?.response?.status === 404) return barcode;
        console.error("Could not load the account's health record, so which record this profile uses can't be told.", error);
        return null;
    }
}

/**
 * The checkout request with the buyer's health record id added, when it can be told.
 *
 * A genetic report's results are stored against this id. Without it, PM had to work the
 * patient out from the barcode, which cannot tell a profile that changed hands from one that
 * didn't. Only added for the profile the customer is on: the epigenetic upgrade modal buys
 * for a kit barcode of its own, whose record this is not.
 */
export async function withHealthUserId(order) {
    if (!order || order.barcode !== store.getters.profile?.profile?.barcode) return order;
    const patientUuid = await resolveHealthUserId();
    return patientUuid ? { ...order, patientUuid } : order;
}

export function getHealthUserId() {
    let user;
    if (store.getters.profile?.permission?.type === 'OWNER') {
        const ownersList = store.getters.owners;
        if (ownersList.length > 1) {
            const matchedOwner = ownersList.find(owner => owner.barcode === store.getters.profile.profile.barcode && owner.barcode === store.getters.primaryPatient.linkedProfile);
            if (matchedOwner) {
                console.log("Matched Owner:", matchedOwner);
                user = store.getters.account.uuid;
            } else {
                console.log("Owner with the barcode not found.");
                user = store.getters.profile.profile.barcode;
            }  
        } else {
            user = store.getters.account.uuid;
        }
    } else {
        user = store.getters.profile.profile.barcode;
    }
    return user;
}

export async function makeHealthRequest(data) {
    try {
        return axios.request({
            url: `${VUE_APP_API}/health`,
            data: data,
            method: "post",
            headers: {
                authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
        });
    } catch (error) {
        console.error("Error: ", error);
    }
}

export async function getTestsForUser(userId) {
    return await makeHealthRequest({
        path: `/patients/${userId}/tests`,
        method: "GET",
    });
}

async function getKitAndOrderForTest(barcode) {
    const { data } = await makeHealthRequest({
        method: "GET",
        path: `/kits/${barcode}`,
    });
    const order = JSON.parse(data.order.rawJson);
    return { kit: data, order };
}

async function getResult(barcode) {
    const user = getHealthUserId();
    return await makeHealthRequest({
        method: "GET",
        path: `/reports/${barcode}/patients/${user}`,
    });
}

export async function getUnconfirmedAppointments(user) {
    const { data } = await makeHealthRequest({
        path: `/orders/patient/${user.email}/missingAppointmentData`,
        method: "GET",
    });
    return data;
}

export function isHealthEnabled() {
    return true;
}

export async function checkPatientAtActivation(userUuid) {
    try {
        const patient = await makeHealthRequest({
            path: `/patients/${userUuid}`,
            method: "GET",
        });
        return patient.data;
    } catch (e) {
        console.error(e);
        return false;
    }
}

export function shouldCreateHealthProfile(activatedTest) {
    const validSkus = [
        "LDNA-YULIFE-UK",
        "LDNA-GROUPON-AFN-ROW",
        "LDNA-GROUPON-AFN-AU",
        "LDNA-GROUPON-AFN-CA",
        "LDNA-GROUPON-AFN-EU",
        "LDNA-GROUPON-AFN-NZ",
        "LDNA-GROUPON-AFN-UK",
        "LDNA-GROUPON-AFN-US"
    ];
    return validSkus.includes(activatedTest.product_sku);
}

export const useNewWellbeing = computed(
    () => store.getters.selectedTest.product_sku === "LDNA-YULIFE-UK"
);

export const useNewBaker = computed(
    () => store.getters.selectedTest.product_sku === "LDNA-YULIFE-UK"
);

export async function createHealthProfile(healthProfileDetails) {
    return await makeHealthRequest({
        path: "/patients",
        method: "POST",
        body: healthProfileDetails,
    });
}

export async function getTestDetailsForKit(kit) {
    let details;
    details = testDetails.find((test) => test.SKU === kit.sku);
    if (!details) {
        details = testDetails.find((test) => test["WP SKU"] === kit.sku);
        if (!details) {
            throw Error(`Details could not be found for ${kit.sku}`);
        }
    }
    return details;
}

function mapGeneticResult(result) {
    if (!result) {
        return false;
    }
    const [meta, results] = result;
    return {
        ...meta,
        results,
    };
}

function mapResult(result) {
    return result;
}

function formatTestWithResult(test, result, kit, order) {
    const parsedResult = JSON.parse(result.data.rawJson);
    return {
        ...test,
        name: kit.testType === "bloodTest" ? getTestDetailsForKit(kit).Name : "",
        image: kit.testType === "bloodTest" ? getTestDetailsForKit(kit).Image : "",
        questionnaireAnswers: JSON.parse(test.questionnaireAnswers),
        result:
            kit.testType === "bloodTest" ? mapResult(parsedResult) : mapGeneticResult(parsedResult),
        kit,
        order,
        craftSlug: kit.testType === "bloodTest" ? getTestDetailsForKit(kit)["Craft Slug"] : "",
    };
}

export function getInstanceName(location) {
    console.log("location", location);
    const instanceDict = {
        1: "First",
        2: "Second",
        3: "Third",
        4: "Fourth",
        5: "Fifth",
        6: "Sixth",
        7: "Seventh",
        8: "Eigth",
        9: "Ninth",
        10: "Tenth",
    };
    return instanceDict[location];
}

export function mostRecentFirst(a, b) {
    return Date.parse(b.createdAt) - Date.parse(a.createdAt);
}

function getAgeGroupForPatient(patient) {
    const age = new Date().getFullYear() - new Date(patient.dateOfBirth).getFullYear();
    if (age < 30) {
        return "18-29";
    }
    if (age < 40) {
        return "30-39";
    }
    if (age < 50) {
        return "40-49";
    }
    if (age < 60) {
        return "50-59";
    }
    if (age < 70) {
        return "60-69";
    }
    if (age < 80) {
        return "70-79";
    }
    if (age < 90) {
        return "80-89";
    }
    if (age < 100) {
        return "90-99";
    }
    return "over100";
}

export async function createPatient(patientDetails) {
    return await makeHealthRequest({
        path: "/patients",
        method: "POST",
        body: patientDetails,
    });
}

export async function checkPatient() {
    const user = getHealthUserId();
    try {
        const patient = await makeHealthRequest({
            path: `/patients/${user}`,
            method: "GET",
        });
        return patient.data;
    } catch (e) {
        console.error(e);
        return false;
    }
}

export async function getPatient() {
    const user = getHealthUserId();
    const fullPatient = await makeHealthRequest({
        path: `/patients/${user}`,
        method: "GET",
    });
    const tests = await getTestsForUser(user);

    const formattedTests = await Promise.all(
        tests.data.map(async (test) => {
            const { kit, order } = await getKitAndOrderForTest(test.barcode);

            try {
                const result = await getResult(test.barcode);
                if (result) {
                    return formatTestWithResult(test, result, kit, order);
                }
            } catch (e) {
                //   return formatTestWithoutResult(test, kit, order);
            }
        })
    );
    // .sort(mostRecentFirst)
    // .map((test, index, fullArr) => {
    //     return {
    //         ...test,
    //         instance: getInstanceName(
    //         (fullArr.filter((test) => test?.kit?.testType === "bloodTest").length - index)
    //         ),
    //     };
    // });

    const formattedBloodTests = formattedTests.filter((test) => {
        {
            return test?.kit.testType === "bloodTest";
        }
    });

    // const [latestTest] = formattedBloodTests;

    const geneticTest = formattedTests.find((test) => {
        return test?.kit.testType === "geneticTest";
    });

    const patient = {
        ...fullPatient.data,
        ageGroup: getAgeGroupForPatient(fullPatient.data),
        tests: formattedBloodTests,
        questionnaireAnswers: JSON.parse(fullPatient.data.questionnaireAnswers),
        geneticTest: geneticTest ? geneticTest : false,
    };

    if (patient.geneticTest?.result) {
        patient.geneticTest.result.hasAnyResult =
            patient.geneticTest.result.results?.filter(
                (r) => !["Locked", "Awaiting Results"].includes(r.result_value)
            ).length > 0;
    }
    // mostRecentTest.value = latestTest;

    return patient;
}

export async function saveQuestionnaireAnswers({ answers }) {
    const user = getHealthUserId();
    const { data } = await makeHealthRequest({
        path: `/patients/${user}/answers`,
        method: "PUT",
        body: {
            userUuid: user,
            answers: JSON.stringify(answers),
        },
    });
    return data;
}

/**
 * The barcode, SKU and activation state for an activation code, and nothing else.
 *
 * Deliberately not GET /kits/{code}: that also carries the order, its cart rows and the
 * shipment tracking, which forced the proxy to let any signed-in user read an unclaimed
 * kit. This endpoint is narrow enough to serve before anyone owns the kit, so the full
 * kit read can require ownership.
 */
export async function checkHealthKitActivationCode(code) {
    const { data } = await makeHealthRequest({
        path: `/kits/${code}/activation-summary`,
        method: "GET",
    });
    return data;
}

export async function activateHealthKit(testerDetails, kit) {
    const { data } = await makeHealthRequest({
        path: `/patients/${testerDetails.userUuid}/activation`,
        method: "PUT",
        body: { ...testerDetails, barcode: kit.barcode },
    });
    return data;
}

export async function hasHealthAccess() {
    const patientStore = usePatientStore();
    if (!patientStore.patient.linkedProfile) {
        await patientStore.getPatient();
    }
    const isLinked = patientStore?.patient?.linkedProfile;
    const isProfile = store.getters.profile.profile.barcode;
    //console.log('Checking Health Access:', { isLinked, isProfile });
    return isLinked === isProfile;
    //return store.getters.profile.permission.type === 'OWNER';
}

export async function checkProfileManager(barcode) {
  try {
    const response = await apiClient.get(endpoints.profileManager(barcode));
    // Handle 200 OK response
    if (response.status === 200) {
      return response.data.email === response.data.owneremail;
    }
  } catch (error) {
    // Handle specific error statuses
    if (error.response) {
      switch (error.response.status) {
        case 403: // Forbidden
          console.error('Access denied to profile:', error.response.data.errors);
          return false;
          
        case 404: // Not Found
          console.error('Profile not found:', error.response.data.errors);
          return false;
          
        default: // Other errors (402, 500, etc.)
          console.error('Unexpected API error:', error.response.status, error.response.data);
          throw new Error(`API request failed: ${error.response.status}`);
      }
    } else {
      // Network/request errors (no response)
      console.error('Network error:', error.message);
      throw new Error('Network request failed');
    }
  }
}